/**
 * ServiceOwnerHome (B3) -- "Your services".
 *
 * The Service Owner landed on the estate-wide page and never saw HIS
 * service, because until 0162 nothing in the model could answer which
 * of them was his.
 *
 * Written in words, not chips-only: he is not a resilience specialist,
 * and "tolerance: null" tells him nothing. Each row says what state the
 * thing is in and what, if anything, is being asked of him.
 */
import { itemTitle } from "shared/work/itemTitle";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatDate } from "shared/time/formatDate";

import { roleLabel } from "shared/vocab/workflowState";

import { ToleranceBadge } from "shared/components/ToleranceBadge";
import {
  byIbsId,
  useToleranceState,
  type ToleranceStateRow,
} from "shared/tolerance/useToleranceState";

import { useMe } from "shared/api/useMe";
import { Card } from "shared/components/Card";
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useMyWork } from "shared/work/useMyWork";
import { useMyEstate, type OwnedIBS, type OwnedService } from "twin/home/useMyEstate";

function Findings({ n }: { n: number }) {
  const { t } = useTranslation();
  if (n === 0) {
    return <span className="text-xs text-slate-500">{t("ownerHome.noIncidents")}</span>;
  }
  return (
    <span className="text-xs font-medium text-rag-red">
      {t("ownerHome.openIncidents", { count: n })}
    </span>
  );
}

function ServiceRow({ svc }: { svc: OwnedService }) {
  return (
    <li
      data-testid={`owner-service-${svc.code}`}
      className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-2 last:border-0"
    >
      <Link
        to={`/twin/service/${svc.id}`}
        className="min-w-0 flex-1 truncate text-sm text-slate-800 hover:text-brand-brassDark"
      >
        {svc.name}
      </Link>
      <Findings n={svc.open_findings} />
    </li>
  );
}

function IBSRow({
  ibs,
  tolerance,
}: {
  ibs: OwnedIBS;
  tolerance: ToleranceStateRow | undefined;
}) {
  const { t, i18n } = useTranslation();
  return (
    <li
      data-testid={`owner-ibs-${ibs.code}`}
      className="border-b border-slate-100 py-2 last:border-0"
    >
      <div className="flex items-baseline justify-between gap-3">
        <Link
          to={`/twin/ibs/${ibs.id}`}
          className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 hover:text-brand-brassDark"
        >
          {ibs.name}
        </Link>
        <Findings n={ibs.open_findings} />
      </div>
      <p className="mt-0.5 text-xs text-slate-600">
        {/* In words. "tolerance: null" tells an owner nothing.
            D2 -- "2h" alone reads as a governed figure. Most are not
            signed for, and the owner is the one who would have to
            chase it, so the badge says which.
            C1 -- and the badge is the ONLY thing that says it. This
            branched on the hours column first, so an owner who had
            just declared 2h read "No impact tolerance declared yet":
            the column does not fill until the second signature, and
            his own work was invisible until somebody else acted. */}
        {tolerance ? (
          <ToleranceBadge state={tolerance} />
        ) : (
          <span
            data-testid={`owner-ibs-${ibs.code}-no-tolerance`}
            className="text-amber-800"
          >
            {t("ownerHome.toleranceMissing")}
          </span>
        )}
        {ibs.admission_status === "admitted" && ibs.admitted_on && (
          <>
            {" · "}
            <span
              data-testid={`owner-ibs-${ibs.code}-admitted`}
              className="text-emerald-800"
            >
              {t(
                ibs.service_class === "internal"
                  ? "ownerHome.admittedOnInternal"
                  : "ownerHome.admittedOn",
                { date: formatDate(ibs.admitted_on, i18n.language) },
              )}
            </span>
          </>
        )}
        {ibs.admission_status !== "admitted" && (
          <>
            {" · "}
            <span className="text-amber-800">
              {t(
                ibs.service_class === "internal"
                  ? "ownerHome.notYetAdmittedInternal"
                  : "ownerHome.notYetAdmitted",
              )}
            </span>
          </>
        )}
      </p>
    </li>
  );
}

export function ServiceOwnerHome() {
  const { t } = useTranslation();
  const me = useMe();
  const estate = useMyEstate();
  const work = useMyWork();
  const tolerance = byIbsId(useToleranceState().data);

  const mine = (work.data?.items ?? []).filter((w) => w.ownership !== "queue");
  const services = estate.data?.services ?? [];
  // Round 16 L1 -- a Critical Internal Function is on its own register,
  // and the treasurer read his function under "Important business
  // services you own", "not yet admitted as an important business
  // service". Removed rows are on neither list.
  const owned = (estate.data?.ibses ?? []).filter(
    (i) => i.admission_status !== "removed",
  );
  const ibses = owned.filter((i) => i.service_class !== "internal");
  const functions = owned.filter((i) => i.service_class === "internal");
  const nothingMatched = estate.data !== undefined && estate.data.matched_on === null;
  // "Nothing is recorded against you" is an ANSWER, and rendering it
  // while the question is still in flight states it before it is known.
  const loading = estate.isLoading;

  return (
    <article data-testid="owner-home" className="space-y-6">
      <header>
        <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {t("ownerHome.rubric")}
        </p>
        {/* Round 17 H34 -- an h1 again. Round 13 made it an h2 because
            the hero carried the page's h1; the hero has since stepped
            aside for an owner (it returns nothing for the same seat
            this desk serves), which left the page with no h1 at all,
            third round. One h1, and it is this one. */}
        <h1 className="mt-1 font-display text-2xl text-slate-900">
          <MarsadIcon
            name="ibs"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("ownerHome.heading", {
            name: me.data?.display_name ?? me.data?.username ?? "",
          })}
        </h1>
      </header>

      {/* What is being asked of him, and what that MEANS. He is not a
          resilience specialist; a queue of ceremony names is not an
          instruction. */}
      {mine.length > 0 && (
        <Card micro={t("ownerHome.asked.heading")} icon="dual-signoff">
          <p className="mt-1 text-sm text-slate-700">
            {t("ownerHome.asked.explainer")}
          </p>
          {/* Round 16 M3: the badges went unexplained for fourteen
              rounds. Taught once, here, where they are first met. */}
          <FirstMeetings terms={["owner_roles"]} className="mt-2" />
          <ul className="mt-2 space-y-1.5" data-testid="owner-asked">
            {mine.slice(0, 5).map((w) => (
              <li key={w.id}>
                <Link
                  to="/my-work"
                  className="flex items-baseline gap-2 text-sm text-slate-800 hover:text-brand-brassDark"
                >
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-medium uppercase text-amber-900">
                    {roleLabel(w.my_role)}
                  </span>
                  {/* Round 18 I18 -- the server's title by its key, as My Work reads it. */}
                  <span className="truncate">{itemTitle(t, w)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {functions.length > 0 && (
        <Card micro={t("ownerHome.functions.heading")} icon="ibs">
          {/* Round 16 M7: a treasurer met his class and the funding
              link with nowhere to learn what either meant. */}
          <FirstMeetings
            terms={["critical_internal_function", "funding_edge"]}
            className="mt-2"
          />
          <ul className="mt-1" data-testid="owner-functions">
            {functions.map((i) => (
              <IBSRow key={i.id} ibs={i} tolerance={tolerance.get(i.id)} />
            ))}
          </ul>
        </Card>
      )}

      {(ibses.length > 0 || functions.length === 0) && (
        <Card micro={t("ownerHome.ibses.heading")} icon="ibs">
          {loading ? (
            <p className="mt-2 text-sm text-slate-400">{t("ownerHome.loading")}</p>
          ) : ibses.length === 0 ? (
            <p data-testid="owner-ibses-empty" className="mt-2 text-sm text-slate-500">
              {nothingMatched ? t("ownerHome.noIdentity") : t("ownerHome.ibses.empty")}
            </p>
          ) : (
            <>
              <FirstMeetings terms={["ibs"]} className="mt-2" />
              <ul className="mt-1" data-testid="owner-ibses">
                {ibses.map((i) => (
                  <IBSRow key={i.id} ibs={i} tolerance={tolerance.get(i.id)} />
                ))}
              </ul>
            </>
          )}
        </Card>
      )}

      <Card micro={t("ownerHome.services.heading")} icon="constellation">
        {loading ? (
          <p className="mt-2 text-sm text-slate-400">{t("ownerHome.loading")}</p>
        ) : services.length === 0 ? (
          <p data-testid="owner-services-empty" className="mt-2 text-sm text-slate-500">
            {nothingMatched ? t("ownerHome.noIdentity") : t("ownerHome.services.empty")}
          </p>
        ) : (
          <ul className="mt-1" data-testid="owner-services">
            {services.map((s) => (
              <ServiceRow key={s.id} svc={s} />
            ))}
          </ul>
        )}
      </Card>
    </article>
  );
}
